import { useQueryClient } from '@tanstack/react-query';
import { AxiosError, getCustomerServiceUrl, isLocalhost, prependMarketToPath } from 'common';
import { t } from 'i18n';
import { ApiErrorType } from 'models';
import { useEffect, useState } from 'react';
import { useNavigate, useRouteError } from 'react-router-dom';
import { AwardError, EuroBonusAccountErrorType, HttpStatusCode, RatelimitErrorReponse } from 'shared';

import { Path } from '../domain/constants';
import { NoAuthCookieError } from '../domain/errors';
import { useBookingStore } from '../domain/store';
import { useFlightsPrefix } from '../hooks/useFlights';

import { ErrorModal } from './ErrorModal';

export function RootBoundary() {
  const error = useRouteError();

  if (error instanceof NoAuthCookieError) {
    return <UnauthorizedModal isApiOrigin={false} />;
  }

  if (error instanceof AxiosError) {
    const status = error.response?.status;

    if (status === HttpStatusCode.Unauthorized) {
      return <UnauthorizedModal isApiOrigin={true} />;
    }

    if (status === HttpStatusCode.Forbidden) {
      const reason = error.response?.data?.bookingErrorType;

      if (reason === EuroBonusAccountErrorType.MISSING || reason === EuroBonusAccountErrorType.NOT_OF_TYPE_EURO_BONUS) {
        return <EuroBonusAccountModal isMissingAccount={true} />;
      }

      if (
        reason === EuroBonusAccountErrorType.NOT_ACTIVE ||
        reason === EuroBonusAccountErrorType.INVALID_FIELDS ||
        reason === EuroBonusAccountErrorType.REDEMPTION_DISABLED
      ) {
        return <EuroBonusAccountModal isMissingAccount={false} />;
      }

      if (reason === EuroBonusAccountErrorType.PSG_DID_NOT_MATCH) {
        return <EuroBonusAccountPSGErrorModal />;
      }
    }

    if (status === HttpStatusCode.NotFound) {
      const reason = error.response?.data?.bookingErrorType;
      if (reason === ApiErrorType.FARE_NOT_AVAILABLE) {
        return <FareNotAvailableErrorModal />;
      }
    }

    if (status === HttpStatusCode.TooManyRequests) {
      return <TooManyRequestsErrorModal {...(error.response?.data ?? {})} />;
    }

    if (status === HttpStatusCode.BadRequest) {
      const reason = error.response?.data?.bookingErrorType;
      if (reason === AwardError.OUTBOUND_INBOUND_TOO_CLOSE) {
        return <OutboundAndInboundTooCloseErrorModal />;
      }
    }
  }

  return <UnexpectedErrorModal />;
}

function UnauthorizedModal({ isApiOrigin }: { isApiOrigin: boolean }) {
  const queryClient = useQueryClient();
  const state = useBookingStore();

  const handleClick = () => {
    queryClient.clear();
    state.clear();

    location.href = isLocalhost() ? 'https://test.sas.se/login' : prependMarketToPath('/login');
  };

  if (isApiOrigin) {
    return (
      <ErrorModal
        title={t('errorModal.sessionTimeout.title', { defaultValue: 'Session timed out' })}
        buttonLabel={t('common:ok', { defaultValue: 'Ok' })}
        onClick={handleClick}
      >
        <p>
          {t('errorModal.sessionTimeout.body', {
            defaultValue:
              'We tried to hold your booking, but your session has expired. Please start your booking again.',
          })}
        </p>
      </ErrorModal>
    );
  }

  return (
    <ErrorModal
      title={t('errorModal.noSession.title', { defaultValue: 'Sign in required' })}
      buttonLabel={t('common:login', { defaultValue: 'Log In' })}
      onClick={handleClick}
    >
      <p>
        {t('errorModal.noSession.body', {
          defaultValue: 'To continue, please log in to your EuroBonus account and try again.',
        })}
      </p>
    </ErrorModal>
  );
}

// Unexpected Client Error OR UNKNOWN server errors
function UnexpectedErrorModal() {
  const queryClient = useQueryClient();
  const state = useBookingStore();
  const navigate = useNavigate();

  const handleClick = () => {
    queryClient.clear();
    state.clear();

    navigate(Path.FLIGHTS);
  };

  return (
    <ErrorModal
      title={t('errorModal.unexpected.title', { defaultValue: 'Oops! Something went wrong' })}
      buttonLabel={t('common:ok', { defaultValue: 'Ok' })}
      onClick={handleClick}
    >
      <p>
        {t('errorModal.unexpected.body', {
          defaultValue: 'An error occurred while processing your booking. Please go back and start again.',
        })}
      </p>
    </ErrorModal>
  );
}

function TooManyRequestsErrorModal({ payload }: RatelimitErrorReponse) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [remaingTime, setRemainingTime] = useState(payload?.remainingTime ?? 0);

  const handleClick = () => {
    const queryKeys = queryClient.getQueryCache().findAll();

    queryKeys.forEach(query => {
      const [firstKey] = Array.isArray(query?.queryKey) ? query.queryKey : [];

      if (firstKey === useFlightsPrefix) {
        queryClient.removeQueries(query);
      }
    });

    navigate(Path.FLIGHTS);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setRemainingTime(prev => (prev ? prev - 1 : 0));
    }, 1000);

    return () => clearTimeout(interval);
  }, [payload?.remainingTime]);

  return (
    <ErrorModal
      title={t('errorModal.tooManyRequests.title', { defaultValue: 'Search Limit Reached' })}
      buttonLabel={remaingTime > 0 ? `${remaingTime}` : t('common:ok', { defaultValue: 'Ok' })}
      onClick={handleClick}
      buttonDisabled={remaingTime !== 0}
    >
      <p>
        {t('errorModal.tooManyRequests.body', {
          defaultValue:
            "You've made too many searches in a short time. Please wait a moment and try again. Thank you for your patience.",
        })}
      </p>
    </ErrorModal>
  );
}

function EuroBonusAccountModal({ isMissingAccount }: { isMissingAccount?: boolean }) {
  const queryClient = useQueryClient();
  const state = useBookingStore();

  const handleClick = () => {
    queryClient.clear();
    state.clear();

    location.href = isLocalhost() ? 'https://test.sas.se/' : getCustomerServiceUrl();
  };

  const title = isMissingAccount
    ? t('errorModal.euroBonusMissing.title', { defaultValue: 'EuroBonus account needed' })
    : t('errorModal.euroBonusInvalid.title', { defaultValue: 'EuroBonus account issue' });

  const message = isMissingAccount
    ? t('errorModal.euroBonusMissing.body', {
        defaultValue: 'You need a EuroBonus account to book a flight. Please create one and try again.',
      })
    : t('errorModal.euroBonusInvalid.body', {
        defaultValue: "We're unable to process your booking right now. Please contact customer support for help.",
      });

  return (
    <ErrorModal title={title} buttonLabel={t('common:ok', { defaultValue: 'Ok' })} onClick={handleClick}>
      <p>{message}</p>
    </ErrorModal>
  );
}

function EuroBonusAccountPSGErrorModal() {
  const navigate = useNavigate();

  return (
    <ErrorModal
      title={t('errorModal.euroBonusNumberValidation.title', { defaultValue: 'Incorrect EuroBonus number' })}
      buttonLabel={t('common:ok', { defaultValue: 'Ok' })}
      onClick={() => navigate(Path.TRAVELERS)}
    >
      <p>
        {t('errorModal.euroBonusNumberValidation.body', {
          defaultValue: 'The EuroBonus number you entered seems to be incorrect. Please check it and try again.',
        })}
      </p>
    </ErrorModal>
  );
}

function OutboundAndInboundTooCloseErrorModal() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const handleClick = () => {
    const queryKeys = queryClient.getQueryCache().findAll();

    queryKeys.forEach(query => {
      const [firstKey] = Array.isArray(query?.queryKey) ? query.queryKey : [];

      if (firstKey === useFlightsPrefix) {
        queryClient.removeQueries(query);
      }
    });

    navigate(Path.FLIGHTS);
  };

  return (
    <ErrorModal
      title={t('errorModal.outboundAndInboundTooClose.title', { defaultValue: 'Flight times too close' })}
      buttonLabel={t('common:ok', { defaultValue: 'Ok' })}
      onClick={handleClick}
    >
      <p>
        {t('errorModal.outboundAndInboundTooClose.body', {
          defaultValue:
            'The outbound and inbound flights you selected are too close together. Please choose flights with at least 1 hour between landing and takeoff, and try again.',
        })}
      </p>
    </ErrorModal>
  );
}

function FareNotAvailableErrorModal() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const handleClick = () => {
    const queryKeys = queryClient.getQueryCache().findAll();

    queryKeys.forEach(query => {
      const [firstKey] = Array.isArray(query?.queryKey) ? query.queryKey : [];

      if (firstKey === useFlightsPrefix) {
        queryClient.removeQueries(query);
      }
    });

    navigate(Path.FLIGHTS);
  };

  return (
    <ErrorModal
      title={t('error.fareNotAvailable.title')}
      onClick={handleClick}
      buttonLabel={t('error.fareNotAvailable.button')}
    >
      {t('error.fareNotAvailable.message')}
    </ErrorModal>
  );
}
